import { getExpertiesThunk, getSkillsThunk } from '../../../../../../store/signUp/thunks';
import { selectExpertsList, selectSkillsList } from '../../../../../../store/signUp/selectors';
import { Expert, Experties } from '../../../../../../store/workspace/types';
import { useDispatch, useSelector } from '../../../../../../store/store';
import { useProfileUpdate } from '../../../hooks/useProfileUpdate';
import { FIRST_CUSTOM_SKILL_ID } from '../../../constants';
import { ListItem } from '../../../../../../store/signUp/types';
import Button from '../../../../../../components/Button';
import Modal from '../../../../../../components/Modal';
import Input from '../../../../../../components/Input';
import Tag from '../../../../../../components/Tag';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';

/** An expertise the applicant typed themselves, i.e. one that is not in the list. */
const customExpert = (title: string): Expert => ({ id: -1, title, orderId: 0 });

const startsWith = (title: string, term: string) =>
    title.toLowerCase().startsWith(term.toLowerCase());

interface Props {
    /** The expertise being edited, or `null` when a new one is added. */
    expertise: Experties | null;
    onClose: () => void;
};

/** Adds an expertise and its skills to the profile, or edits one already on it. */
function ExpertiseForm({ expertise, onClose }: Props) {
    const dispatch = useDispatch();
    const skillsList = useSelector(selectSkillsList);
    const expertsList = useSelector(selectExpertsList);
    const { profile, isSaving, saveProfile } = useProfileUpdate();

    const [skillTitle, setSkillTitle] = useState('');
    const [skills, setSkills] = useState<ListItem[]>(expertise?.skills || []);
    const [expert, setExpert] = useState<Expert>(expertise?.expert || customExpert(''));
    const [nextCustomSkillId, setNextCustomSkillId] = useState(FIRST_CUSTOM_SKILL_ID);

    useEffect(() => {
        dispatch(getSkillsThunk());
        dispatch(getExpertiesThunk());
    }, []);

    /** Shortest first, so the tag box stays readable. */
    const addSkill = (skill: ListItem) => {
        setSkillTitle('');
        setSkills(prev => prev.some(i => i.id === skill.id)
            ? prev
            : [...prev, skill].sort((a, b) => a.title.length - b.title.length));
    };

    const addTypedSkill = () => {
        addSkill({ id: nextCustomSkillId, title: skillTitle });
        setNextCustomSkillId(prev => prev - 1);
    };

    const removeSkill = (id: number) => {
        setSkills(prev => prev.filter(i => i.id !== id));
    };

    const buildExperties = (): Experties[] => {
        if (!profile) return [];

        if (expertise) {
            const others = profile.experties.filter(i => i.expert.id !== expertise.expert.id);
            return [...others, { expert, skills }];
        }

        const orders = profile.experties.map(i => i.expert.orderId);
        const orderId = orders.length > 0 ? Math.max(...orders) : 0;
        return [...profile.experties, { expert: { ...expert, orderId }, skills }];
    };

    const submit = async () => {
        if (!profile) return;
        const saved = await saveProfile({ experties: buildExperties() });
        if (saved) onClose();
    };

    return (
        <Modal
            size='md'
            actions='confirm'
            closeFunc={onClose}
            childClassName={styles.wrapper}
            title={expertise ? 'Edit expertise' : 'Add expertise'}
            confirmButton={{
                text: 'Save',
                type: 'Primary',
                onClick: submit,
                loading: isSaving,
                disabled: !expert.title,
            }}
        >
            <Input
                listShow
                type='text'
                listIdKey='id'
                label='Expertise'
                listTitleKey='title'
                value={expert.title}
                placeholder='EX: Software engineer'
                onItemClick={(item: Expert) => setExpert(item)}
                onChange={(val: string) => setExpert(customExpert(val))}
                list={expertsList.filter(i => startsWith(i.title, expert.title))}
            />
            <span className={styles.skills_label}>Your Skills</span>
            <div className={styles.skills}>
                <div className={styles.skills_action}>
                    <Input
                        listShow
                        type='text'
                        listIdKey='id'
                        value={skillTitle}
                        listTitleKey='title'
                        onChange={setSkillTitle}
                        onItemClick={addSkill}
                        placeholder='Enter skill or select from list'
                        wrapperClassName={styles.skills_action_input}
                        list={skillsList.filter(i => startsWith(i.title, skillTitle))}
                    />
                    <Button
                        text='+'
                        type='Outline-Primary'
                        disabled={!skillTitle}
                        onClick={addTypedSkill}
                    />
                </div>
                <div className={styles.skills_box}>
                    {skills.map(item => <Tag
                        key={item.id}
                        title={item.title}
                        action={() => removeSkill(item.id)}
                    />)}
                </div>
            </div>
        </Modal>
    );
};

export default ExpertiseForm;
